import openai from './openaiClient';
import anthropicClient from './anthropicClient';
import genAI from './geminiClient';

/**
 * Analyzes content and provides tone suggestions
 * @param {string} content - Post content to analyze
 * @param {string} platform - Target platform
 * @param {string} provider - AI provider (openai, claude, gemini)
 * @returns {Promise<Object>} Tone analysis and suggestions
 */
export async function analyzeTone(content, platform = 'general', provider = 'openai') {
  if (!content?.trim()) {
    throw new Error('Content is required for tone analysis');
  }

  try {
    if (provider === 'openai') {
      const response = await openai?.chat?.completions?.create({
        model: 'gpt-4o-mini',
        messages: [
          { 
            role: 'system', 
            content: 'You are a social media expert analyzing content tone. Provide concise, actionable feedback.' 
          },
          { 
            role: 'user', 
            content: `Analyze the tone of this ${platform} post and suggest improvements:\n\n"${content}"\n\nProvide:\n1. Current tone (1-2 words)\n2. Tone score (1-10)\n3. Suggested tone improvements\n4. 2-3 alternative tone variations\n\nFormat as JSON with keys: currentTone, toneScore, suggestions, alternatives` 
          },
        ],
        response_format: { type: 'json_object' },
        max_tokens: 500
      });

      return JSON.parse(response?.choices?.[0]?.message?.content);
    } else if (provider === 'claude') {
      const response = await anthropicClient?.post('/messages', {
        model: 'claude-3-5-sonnet-20241022',
        max_tokens: 500,
        messages: [
          {
            role: 'user',
            content: `Analyze the tone of this ${platform} post and suggest improvements:\n\n"${content}"\n\nProvide:\n1. Current tone (1-2 words)\n2. Tone score (1-10)\n3. Suggested tone improvements\n4. 2-3 alternative tone variations\n\nFormat as JSON with keys: currentTone, toneScore, suggestions, alternatives`
          }
        ]
      });

      return JSON.parse(response?.data?.content?.[0]?.text);
    } else if (provider === 'gemini') {
      const model = genAI?.getGenerativeModel({ model: 'gemini-2.0-flash-exp' });
      const result = await model?.generateContent(`Analyze the tone of this ${platform} post and suggest improvements:\n\n"${content}"\n\nProvide:\n1. Current tone (1-2 words)\n2. Tone score (1-10)\n3. Suggested tone improvements\n4. 2-3 alternative tone variations\n\nFormat as JSON with keys: currentTone, toneScore, suggestions, alternatives`);
      const text = result?.response?.text();
      return JSON.parse(text);
    }
  } catch (error) {
    console.error('Error analyzing tone:', error);
    throw new Error(`Failed to analyze tone: ${error?.message}`);
  }
}

/**
 * Generates relevant hashtag suggestions based on content and platform
 * @param {string} content - Post content
 * @param {string} platform - Target platform
 * @param {string} provider - AI provider
 * @returns {Promise<Object>} Hashtag suggestions categorized by type
 */
export async function generateHashtags(content, platform = 'general', provider = 'openai') {
  if (!content?.trim()) {
    throw new Error('Content is required for hashtag generation');
  }

  try {
    if (provider === 'openai') {
      const response = await openai?.chat?.completions?.create({
        model: 'gpt-4o-mini',
        messages: [
          { 
            role: 'system', 
            content: `You are a social media hashtag expert. Generate relevant, trending hashtags optimized for ${platform}.` 
          },
          { 
            role: 'user', 
            content: `Generate hashtags for this ${platform} post:\n\n"${content}"\n\nProvide:\n1. trending: 5-7 trending hashtags\n2. niche: 3-5 niche-specific hashtags\n3. branded: 2-3 branded/campaign hashtags\n4. optimal: Recommended number of hashtags for ${platform}\n\nFormat as JSON with keys: trending (array), niche (array), branded (array), optimal (number)` 
          },
        ],
        response_format: { type: 'json_object' },
        max_tokens: 400
      });

      return JSON.parse(response?.choices?.[0]?.message?.content);
    } else if (provider === 'claude') {
      const response = await anthropicClient?.post('/messages', {
        model: 'claude-3-5-sonnet-20241022',
        max_tokens: 400,
        messages: [
          {
            role: 'user',
            content: `Generate hashtags for this ${platform} post:\n\n"${content}"\n\nProvide:\n1. trending: 5-7 trending hashtags\n2. niche: 3-5 niche-specific hashtags\n3. branded: 2-3 branded/campaign hashtags\n4. optimal: Recommended number of hashtags for ${platform}\n\nFormat as JSON with keys: trending (array), niche (array), branded (array), optimal (number)`
          }
        ]
      });

      return JSON.parse(response?.data?.content?.[0]?.text);
    } else if (provider === 'gemini') {
      const model = genAI?.getGenerativeModel({ model: 'gemini-2.0-flash-exp' });
      const result = await model?.generateContent(`Generate hashtags for this ${platform} post:\n\n"${content}"\n\nProvide:\n1. trending: 5-7 trending hashtags\n2. niche: 3-5 niche-specific hashtags\n3. branded: 2-3 branded/campaign hashtags\n4. optimal: Recommended number of hashtags for ${platform}\n\nFormat as JSON with keys: trending (array), niche (array), branded (array), optimal (number)`);
      const text = result?.response?.text();
      return JSON.parse(text);
    }
  } catch (error) {
    console.error('Error generating hashtags:', error);
    throw new Error(`Failed to generate hashtags: ${error?.message}`);
  }
}

/**
 * Provides platform-specific formatting suggestions
 * @param {string} content - Post content
 * @param {string[]} platforms - Target platforms
 * @param {string} provider - AI provider
 * @returns {Promise<Object>} Platform-specific formatting recommendations
 */
export async function suggestPlatformFormatting(content, platforms = [], provider = 'openai') {
  if (!content?.trim()) {
    throw new Error('Content is required for formatting suggestions');
  }

  if (!platforms?.length) {
    throw new Error('At least one platform is required');
  }

  try {
    const platformList = platforms?.join(', ');
    
    if (provider === 'openai') {
      const response = await openai?.chat?.completions?.create({
        model: 'gpt-4o-mini',
        messages: [
          { 
            role: 'system', 
            content: 'You are a social media formatting expert. Provide platform-specific optimization suggestions.' 
          },
          { 
            role: 'user', 
            content: `Optimize this post for ${platformList}:\n\n"${content}"\n\nFor each platform, provide:\n1. formattedContent: Optimized version\n2. characterCount: Character count\n3. suggestions: Array of specific improvements\n4. bestPractices: Array of platform best practices\n\nFormat as JSON with platform names as keys (facebook, instagram, twitter, linkedin, tiktok)` 
          },
        ],
        response_format: { type: 'json_object' },
        max_tokens: 800
      });

      return JSON.parse(response?.choices?.[0]?.message?.content);
    } else if (provider === 'claude') {
      const response = await anthropicClient?.post('/messages', {
        model: 'claude-3-5-sonnet-20241022',
        max_tokens: 800,
        messages: [
          {
            role: 'user',
            content: `Optimize this post for ${platformList}:\n\n"${content}"\n\nFor each platform, provide:\n1. formattedContent: Optimized version\n2. characterCount: Character count\n3. suggestions: Array of specific improvements\n4. bestPractices: Array of platform best practices\n\nFormat as JSON with platform names as keys (facebook, instagram, twitter, linkedin, tiktok)`
          }
        ]
      });

      return JSON.parse(response?.data?.content?.[0]?.text);
    } else if (provider === 'gemini') {
      const model = genAI?.getGenerativeModel({ model: 'gemini-2.0-flash-exp' });
      const result = await model?.generateContent(`Optimize this post for ${platformList}:\n\n"${content}"\n\nFor each platform, provide:\n1. formattedContent: Optimized version\n2. characterCount: Character count\n3. suggestions: Array of specific improvements\n4. bestPractices: Array of platform best practices\n\nFormat as JSON with platform names as keys (facebook, instagram, twitter, linkedin, tiktok)`);
      const text = result?.response?.text();
      return JSON.parse(text);
    }
  } catch (error) {
    console.error('Error suggesting platform formatting:', error);
    throw new Error(`Failed to suggest platform formatting: ${error?.message}`);
  }
}

/**
 * Comprehensive content enhancement - combines all suggestions
 * @param {string} content - Post content
 * @param {string[]} platforms - Target platforms
 * @param {string} provider - AI provider
 * @returns {Promise<Object>} Complete enhancement suggestions
 */
export async function enhanceContent(content, platforms = [], provider = 'openai') {
  try {
    const [toneAnalysis, hashtags, formatting] = await Promise.all([
      analyzeTone(content, platforms?.[0] || 'general', provider),
      generateHashtags(content, platforms?.[0] || 'general', provider),
      suggestPlatformFormatting(content, platforms, provider)
    ]);

    return {
      tone: toneAnalysis,
      hashtags,
      formatting,
      timestamp: new Date()?.toISOString()
    };
  } catch (error) {
    console.error('Error enhancing content:', error);
    throw new Error(`Failed to enhance content: ${error?.message}`);
  }
}